import React, { useState, useEffect, useRef } from 'react'
import PropTypes from 'prop-types'
import { DragDropProvider } from '@dnd-kit/react'
import { useSortable, isSortable } from '@dnd-kit/react/sortable'
import { connect } from 'react-redux'
import { cloneDeep, isEqual } from 'lodash'
import { useTranslation } from 'react-i18next'

import {
  getChecklistsSettings,
  setChecklistsSettings,
} from '../../../../../../../reducers/Organisation/checklistsReducer'
import Icon from '../../../../../../../components/Icons'
import SearchField from '../../../../../../../components/FormLegacy/FormFieldsV2/SearchField'
import ConfirmPopup from '../../../../../../../components/Popups/ConfirmPopup'
import ChecklistsUpdateCustomTaskModal from '../../../../ChecklistsCreateCustomTaskModal/ChecklistsUpdateCustomTaskModal'
import TaskItem from '../../../../components/TaskItem/TaskItem'
import { reorder } from '../../../../helpers'

const HOW_TO_GET_STARTED_URL =
  'https://help.elevateos.com/checklists-configuring-move-in-or-resident-task-checklists'

const SortableTaskItem = ({ task, index, updateTaskActivity, onEdit }) => {
  const { ref, isDragging } = useSortable({ id: task.id, index })
  return (
    <div
      ref={ref}
      style={{
        marginBottom: 15,
        backgroundColor: isDragging ? '#FDF9F4' : '#ffffff',
      }}
    >
      <TaskItem updateTaskActivity={updateTaskActivity} onEdit={onEdit} task={task} isActive />
    </div>
  )
}

const ChecklistsSetup = ({
  checklistsSettings,
  selectedBuilding,
  isSiteLoading,
  getChecklistsSettings = () => {},
  setChecklistsSettings = () => {},
  setIsUpdated = () => {},
  onSaveChanges = () => {},
  setSiteLoading = () => {},
}) => {
  const { t } = useTranslation(['checklists-settings', 'common'])
  const [searchValue, setSearchValue] = useState('')
  const [changeActivityTask, setChangeActivityTask] = useState(null)
  const [editedTask, setEditedTask] = useState(null)
  const [isEqualSettings, setIsEqualSettings] = useState(true)
  const previous = useRef()

  useEffect(() => {
    setIsEqualSettings(isEqual(previous.current, checklistsSettings))
  }, [selectedBuilding, checklistsSettings, isSiteLoading])

  const { active = [], notActive = [] } = checklistsSettings
  const activeSorted = active
    .sort((a, b) => (a.position > b.position ? 1 : 0))
    .filter((task) => task.name.toLowerCase().includes(searchValue.toLowerCase()))

  const onChangeTaskActivity = (selectedTask) => {
    if (!selectedTask) return

    const activityName = selectedTask.isActive ? 'active' : 'notActive'
    const newSettings = cloneDeep(checklistsSettings)
    const taskIndex = newSettings[activityName].findIndex((task) => task.id === selectedTask.id)

    if (taskIndex > -1) {
      const [removed] = newSettings[activityName].splice(taskIndex, 1)
      const updatedTask = {
        ...removed,
        isActive: !selectedTask.isActive,
        position: null,
      }

      const newActivityName = updatedTask.isActive ? 'active' : 'notActive'

      const updatedSettings = {
        ...newSettings,
        [newActivityName]: [...newSettings[newActivityName], updatedTask],
      }

      const newActiveSorted = updatedSettings['active']
        .sort((a, b) => (a.position > b.position ? 1 : 0))
        .map((task, index) => {
          const updatedTask = { ...task }
          updatedTask.position = index
          return updatedTask
        })

      setChecklistsSettings({
        ...updatedSettings,
        active: newActiveSorted,
      })
      setChangeActivityTask(null)
    }
  }

  const updateTaskActivity = (task) => {
    if (task.type === 'task') {
      onChangeTaskActivity(task)
    } else {
      setChangeActivityTask(task)
    }
  }

  const onDragEnd = (event) => {
    const { operation, canceled } = event
    if (canceled) return
    const { source } = operation
    if (!isSortable(source)) return
    const sourceIndex = source.initialIndex
    const targetIndex = source.index
    if (sourceIndex === targetIndex) return
    const updatedTasks = reorder([...activeSorted], sourceIndex, targetIndex).map(
      (task, index) => ({ ...task, position: index }),
    )
    setChecklistsSettings({ active: updatedTasks, notActive })
  }

  const fetchChecklistsSettings = async () => {
    try {
      setSiteLoading(true)
      const result = await getChecklistsSettings(selectedBuilding.id)
      previous.current = result
    } catch ({ message }) {
      console.error(message)
    } finally {
      setSiteLoading(false)
    }
  }

  const getConfirmText = () =>
    changeActivityTask.isActive
      ? t('checklists-settings:manageInspection.confirmDeactivateBody', {
          type: changeActivityTask.type,
        })
      : t('checklists-settings:manageInspection.confirmActivateBody', {
          type: changeActivityTask.type,
        })

  useEffect(() => {
    if (selectedBuilding) {
      fetchChecklistsSettings()
    }
  }, [selectedBuilding])

  useEffect(() => {
    setIsUpdated(!isEqualSettings)
  }, [isEqualSettings])

  const handleSaveChanges = async () => {
    await onSaveChanges()
    previous.current = cloneDeep(checklistsSettings)
    setIsEqualSettings(true)
  }

  return (
    <React.Fragment>
      <div className={`ctp-checklists-setup ${isSiteLoading ? 'is-loading' : ''}`}>
        <div className="ctp-checklists-setup__toolbar">
          <div>
            <button
              className="button button-black-outline"
              onClick={() => window.open(HOW_TO_GET_STARTED_URL, '_blank', 'noopener,noreferrer')}
            >
              <Icon icon="circleQuestion" />
              {t('checklists-settings:setup.howToGetStartedButton')}
            </button>
          </div>
          <div>
            <SearchField
              placeholder={t('checklists-settings:shared.searchTaskNamePlaceholder')}
              value={searchValue}
              onChange={(e) => setSearchValue(e.target.value)}
            />
            <button
              className="button button-primary"
              onClick={handleSaveChanges}
              disabled={isEqualSettings}
            >
              {t('checklists-settings:shared.saveChangesButton')}
            </button>
          </div>
        </div>
        <div className="ctp-checklists-setup__content">
          <div className="ctp-checklists-setup__content__task-items">
            <div className="ctp-checklists-setup__content__task-items-count">
              {activeSorted.map((item, index) => (
                <span key={item.id}>{`${index + 1}.`}</span>
              ))}
            </div>
            <DragDropProvider onDragEnd={onDragEnd}>
              <div style={{ width: '100%' }}>
                {activeSorted.map((task, index) => (
                  <SortableTaskItem
                    key={task.id}
                    task={task}
                    index={index}
                    updateTaskActivity={updateTaskActivity}
                    onEdit={(task) => setEditedTask(task)}
                  />
                ))}
              </div>
            </DragDropProvider>
          </div>
          <div className="ctp-checklists-setup__divider" />
          <div className="ctp-checklists-setup__content__task-items inactive">
            {notActive
              .filter((task) => task.name.toLowerCase().includes(searchValue.toLowerCase()))
              .map((task) => (
                <TaskItem
                  key={task.id}
                  updateTaskActivity={updateTaskActivity}
                  onEdit={(task) => setEditedTask(task)}
                  isActive={false}
                  task={task}
                />
              ))}
          </div>
        </div>
      </div>
      {changeActivityTask && changeActivityTask.type !== 'task' && (
        <ConfirmPopup
          title={t('checklists-settings:manageInspection.confirmationTitle')}
          body={getConfirmText()}
          cancelText={t('common:actions.cancel')}
          confirmText={t('checklists-settings:manageInspection.yesActivateButton')}
          isOpen={!!changeActivityTask}
          cancelCallback={() => setChangeActivityTask(null)}
          confirmCallback={() => onChangeTaskActivity(changeActivityTask)}
        />
      )}
      {editedTask && (
        <ChecklistsUpdateCustomTaskModal
          onClose={() => setEditedTask(null)}
          selectedBuilding={selectedBuilding}
          taskId={editedTask.id}
        />
      )}
    </React.Fragment>
  )
}

ChecklistsSetup.propTypes = {
  checklistsSettings: PropTypes.object,
  selectedBuilding: PropTypes.object,
  isSiteLoading: PropTypes.bool,
  setIsUpdated: PropTypes.func,
  getChecklistsSettings: PropTypes.func,
  setChecklistsSettings: PropTypes.func,
  onSaveChanges: PropTypes.func,
  setSiteLoading: PropTypes.func,
}

export default connect(null, {
  getChecklistsSettings,
  setChecklistsSettings,
})(ChecklistsSetup)
